import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useTenantStore } from '../store/useTenantStore';
import { usePersistedAuthStore } from '../store/usePersistedAuthStore';
import { useWebView } from '../integrations/webview';

export const InitialRoute: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const tenantId = useTenantStore((state) => state.tenantId);
  const serverRootAddress = useTenantStore((state) => state.serverRootAddress);
  const hasValidTenant = !!tenantId && !!serverRootAddress;
  const refreshToken = usePersistedAuthStore((state) => state.refreshToken);
  
  // WebView integration
  const { isWebView, isAuthenticated: webViewAuthenticated, isLoading: webViewLoading } = useWebView();
  
  // Remove debug logging to reduce spam
  // Only log when there's an actual state change
  React.useEffect(() => {
    console.log('InitialRoute State:', {
      isWebView,
      webViewAuthenticated,
      isAuthenticated,
      hasValidTenant,
      path: window.location.pathname
    });
  }, [isWebView, webViewAuthenticated, isAuthenticated, hasValidTenant]);

  // Check if we should show demo/test whiteboard
  const urlParams = new URLSearchParams(window.location.search);
  const isDemoMode = urlParams.get('demo') === '1' || urlParams.get('test') === '1';
  const isWebViewParam = urlParams.get('isWebView') === 'true';
  
  // If still loading WebView
  if (webViewLoading) {
    return null; // AuthWrapper will show loading
  }
  
  // If URL has isWebView param, go to webview route
  if (isWebViewParam) {
    return <Navigate to="/webview" replace />;
  }
  
  // If in WebView and authenticated, go directly to whiteboard
  if (isWebView && webViewAuthenticated) {
    return <Navigate to="/whiteboard" replace />;
  }
  
  // If demo mode requested, go directly to test whiteboard
  if (isDemoMode) {
    return <Navigate to="/test-external" replace />;
  }

  // If authenticated, check if in WebView
  if (isAuthenticated) {
    // If in WebView, go to whiteboard instead of library
    return <Navigate to={isWebView ? "/whiteboard" : "/library"} replace />;
  }

  // If has refresh token (will be auto-logged in by AuthWrapper)
  if (refreshToken) {
    // If in WebView, go to whiteboard instead of library
    return <Navigate to={isWebView ? "/whiteboard" : "/library"} replace />;
  }

  // If has tenant info, go to login
  if (hasValidTenant) {
    return <Navigate to="/login" replace />;
  }

  // Otherwise, go to org code page
  return <Navigate to="/org-code" replace />;
};